/**
 * The recipe editor's ingredients, one line of text each (#792).
 *
 * A cook writes « 3 L de sauce » the way the recipe prints it, and the line is
 * read into quantity, unit and food with the same parser every imported recipe
 * goes through. The editor used to offer separate boxes, and the two editors
 * disagreed on them: « Modifier » had no unit box at all, wrote `unit: ''` on
 * every save, and kept the stored text of an existing row, so a changed
 * quantity was saved and never shown (the recipe screen prints `original`).
 *
 * A line the cook did not touch keeps its stored ingredient as it was — unit,
 * food and image included — so opening and saving a recipe rewrites nothing.
 */

import type { Recipe, RecipeIngredient } from '../types/recipe';
import {
  abbreviateUnit,
  buildExtendedIngredients,
  formatQuantity,
  parseIngredientText,
  parseNumStr,
} from './ingredientParser';

/** The line the recipe screen prints for one stored ingredient. */
function lineOf(ing: RecipeIngredient): string {
  const original = ing.original?.trim();
  if (original) return original;
  const quantity = ing.amount > 0 ? formatQuantity(ing.amount, ing.unit) : '';
  return [quantity, ing.unit?.trim(), ing.name?.trim()].filter(Boolean).join(' ');
}

/** One line per ingredient, as the editor shows them. */
export function ingredientLinesOf(recipe: Pick<Partial<Recipe>, 'extendedIngredients' | 'ingredients'>): string[] {
  if (recipe.extendedIngredients?.length) {
    return recipe.extendedIngredients.map(lineOf).filter(Boolean);
  }
  return (recipe.ingredients ?? []).map(line => (typeof line === 'string' ? line.trim() : '')).filter(Boolean);
}

/**
 * The stored ingredients for the editor's lines. A line identical to one the
 * recipe already held reuses that ingredient (each stored one at most once);
 * any other line is read afresh. Blank lines are dropped.
 */
export function ingredientsFromLines(
  lines: ReadonlyArray<string>,
  previous: ReadonlyArray<RecipeIngredient> = [],
): RecipeIngredient[] {
  const unused = new Map<string, RecipeIngredient[]>();
  for (const ing of previous) {
    const key = lineOf(ing);
    if (!key) continue;
    const queue = unused.get(key);
    if (queue) queue.push(ing);
    else unused.set(key, [ing]);
  }

  const out: RecipeIngredient[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const kept = unused.get(line)?.shift();
    if (kept) {
      // Older documents carry ingredients with no amount or unit, and Firestore
      // refuses an `undefined` field, so a kept row is completed, never trusted.
      out.push({
        ...kept,
        id: out.length,
        name: kept.name ?? line,
        original: kept.original ?? line,
        amount: Number.isFinite(kept.amount) ? kept.amount : 0,
        unit: kept.unit ?? '',
      });
      continue;
    }
    const [read] = buildExtendedIngredients([line]);
    out.push({ ...read, id: out.length });
  }
  return out;
}

/**
 * How a line will be read, for the hint under the field, or null when there is
 * no quantity to show (« sel au goût » is kept as written).
 */
export function readIngredientLine(line: string): { quantity: string; unit: string; food: string } | null {
  const text = line.trim();
  if (!text) return null;
  const { numPart, unitPart, remainder } = parseIngredientText(text);
  if (!numPart) return null;
  const amount = parseNumStr(numPart);
  if (!(amount > 0)) return null;
  return {
    quantity: formatQuantity(amount, unitPart),
    unit: unitPart ? abbreviateUnit(unitPart) : '',
    food: remainder.trim(),
  };
}

/**
 * The `extendedIngredients` to write for an edited recipe, given what the
 * document holds now. A row whose line is unchanged is written back exactly as
 * stored (`normalized`, `image` and any other field included); only a new or
 * changed line is written from the editor.
 *
 * The editor's own copy is not trusted for the untouched rows: a recipe opened
 * by direct link before the household loads comes through the legacy loader,
 * whose ingredients carry re-derived names, and writing those back rewrote
 * lines nobody edited (#792).
 */
export function ingredientRowsToStore(
  edited: ReadonlyArray<RecipeIngredient>,
  stored: unknown,
): Record<string, unknown>[] {
  const storedRows = Array.isArray(stored)
    ? (stored.filter(row => row && typeof row === 'object') as RecipeIngredient[])
    : [];
  const unused = new Map<string, RecipeIngredient[]>();
  for (const row of storedRows) {
    const key = lineOf(row);
    if (!key) continue;
    const queue = unused.get(key);
    if (queue) queue.push(row);
    else unused.set(key, [row]);
  }

  return edited.map((ing) => {
    const line = (ing.original || lineOf(ing)).trim();
    const kept = unused.get(line)?.shift();
    if (kept) {
      // Firestore refuses `undefined`. Older rows lack amount or unit: those
      // come from the line's own reading, and nothing the row holds is replaced.
      const row: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(kept)) if (value !== undefined) row[key] = value;
      return {
        ...row,
        original: kept.original ?? line,
        name: kept.name ?? ing.name ?? line,
        amount: Number.isFinite(kept.amount) ? kept.amount : Number.isFinite(ing.amount) ? ing.amount : 0,
        unit: kept.unit ?? ing.unit ?? '',
      };
    }
    return {
      original: ing.original,
      normalized: ing.name?.toLowerCase() || '',
      name: ing.name,
      amount: ing.amount,
      unit: ing.unit,
    };
  });
}
